import { useEffect, useRef, useState, type ReactNode } from "react";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { useGetV1Plants } from "@solarkey/api-client";

import { useCapabilities } from "../session/capabilities";

const RECENT_KEY = "sk.recent.scopes";
const MAX_RECENT = 5;

interface DropdownItem {
  id: string;
  label: string;
  /** Secondary text shown right-aligned, e.g. a site's capacity ("42.0 MWp"). */
  meta?: string;
}

interface Segment {
  level: string;
  label: string;
  id: string;
  isCurrent: boolean;
  items: DropdownItem[];
  /** Optional count shown in the dropdown header, e.g. sites in scope. */
  count?: number;
}

/** Scale a capacity Figure (kW/kWp/W) to a "N.N MWp" label; "" when absent. */
function capacityLabel(f?: { value?: number | null; unit?: string | null }): string {
  if (!f || typeof f.value !== "number") return "";
  const divisor = f.unit === "W" ? 1_000_000 : f.unit === "kW" || f.unit === "kWp" ? 1000 : 1;
  const mw = f.value / divisor;
  const n = mw >= 100 ? Math.round(mw).toLocaleString() : mw.toFixed(1);
  return `${n} MWp`;
}

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr: unknown = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((v): v is string => typeof v === "string").slice(0, MAX_RECENT) : [];
  } catch {
    return [];
  }
}

function pushRecent(id: string): void {
  try {
    const prev = readRecent().filter((v) => v !== id);
    localStorage.setItem(RECENT_KEY, JSON.stringify([id, ...prev].slice(0, MAX_RECENT)));
  } catch { /* storage unavailable */ }
}

function parseRouteParams(pathname: string): { tenant?: string; scope?: string; site?: string } {
  const m = pathname.match(/^\/t\/([^/]+)\/s\/([^/]+)/);
  if (!m) return {};
  const tenant = m[1];
  const scope = m[2];
  const siteMatch = pathname.match(/\/plants\/([^/]+)/);
  return { tenant, scope, site: siteMatch?.[1] };
}

export function ScopeBreadcrumb(): ReactNode {
  const caps = useCapabilities();
  const router = useRouter();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { tenant: urlTenant, scope: urlScope, site: urlSite } = parseRouteParams(pathname);

  const plantsQuery = useGetV1Plants(
    { period: "today", scope: urlScope ?? "" },
    { query: { staleTime: 5 * 60_000, enabled: !!urlScope } },
  );
  const plantItems = plantsQuery.data?.status === 200
    ? (plantsQuery.data.data.data.items ?? [])
    : [];
  const siteItems: DropdownItem[] = plantItems.map((it) => ({
    id: it.site_id ?? "",
    label: it.site_id ?? "",
    meta: capacityLabel(it.capacity_dc),
  }));

  const tenantItems = caps.accessible_tenants.map((t) => ({ id: t.slug, label: t.name }));
  const currentTenant = tenantItems.find((t) => t.id === urlTenant) ?? tenantItems[0];

  const scopeItems = caps.scopes.map((s) => ({ id: s.id, label: s.label }));
  const currentScope = scopeItems.find((s) => s.id === urlScope) ?? scopeItems[0];

  const segments: Segment[] = [];

  if (currentTenant) {
    segments.push({
      level: "Tenant",
      label: currentTenant.label,
      id: currentTenant.id,
      isCurrent: !urlScope,
      items: tenantItems,
    });
  }

  if (currentScope && urlScope) {
    segments.push({
      level: "Portfolio",
      label: currentScope.label,
      id: currentScope.id,
      isCurrent: !urlSite,
      items: scopeItems,
    });
  }

  if (urlSite) {
    segments.push({
      level: "Site",
      label: urlSite,
      id: urlSite,
      isCurrent: true,
      items: siteItems,
      count: siteItems.length,
    });
  }

  // PUSH, not replace: switching tenant, portfolio or site is the biggest
  // context change in the product, and with `replace` the previous scope was
  // dropped from history — so Back could not return to where the reader came
  // from (QA, 25 Oct).
  function onSelect(segLevel: string, newId: string): void {
    if (segLevel === "Tenant") {
      void router.navigate({
        to: "/" as never,
      });
    } else if (segLevel === "Portfolio") {
      pushRecent(newId);
      const tenant = urlTenant ?? caps.scopes[0]?.id ?? newId;
      void router.navigate({
        to: `/t/${tenant}/s/${newId}/plants` as never,
      });
    } else if (segLevel === "Site" && urlSite) {
      pushRecent(newId);
      const newPath = pathname.replace(
        `/plants/${urlSite}/`,
        `/plants/${newId}/`,
      );
      void router.navigate({
        to: (newPath || pathname) as never,
      });
    }
  }

  if (segments.length === 0) return null;

  return (
    <nav aria-label="Scope" style={navStyle}>
      {segments.map((seg, i) => (
        <span key={seg.level} style={{ display: "inline-flex", alignItems: "center" }}>
          {i > 0 ? <span style={sepStyle} aria-hidden>/</span> : null}
          {seg.items.length > 0 ? (
            <SegmentDropdown
              segment={seg}
              onSelect={(id) => onSelect(seg.level, id)}
            />
          ) : (
            <span style={seg.isCurrent ? currentStyle : parentStyle}>{seg.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}

/* ── Segment dropdown ──────────────────────────────────────────────── */

function SegmentDropdown({
  segment,
  onSelect,
}: {
  segment: Segment;
  onSelect: (id: string) => void;
}): ReactNode {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setFilter("");
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const recent = readRecent().filter((id) => id !== segment.id);
  const recentItems = recent
    .map((id) => segment.items.find((it) => it.id === id))
    .filter(Boolean) as { id: string; label: string }[];

  const lowerFilter = filter.toLowerCase();
  const filtered = segment.items.filter((it) =>
    it.label.toLowerCase().includes(lowerFilter) || it.id.toLowerCase().includes(lowerFilter),
  );

  return (
    <div style={{ position: "relative" }} ref={ref}>
      <button
        type="button"
        style={segment.isCurrent ? currentStyle : parentStyle}
        aria-expanded={open}
        aria-haspopup="listbox"
        // The name comes from the LEVEL, not the label. A segment's label is a
        // served identifier and can be absent, and the visible text was the
        // button's only accessible name — so an empty label left a button nobody
        // using a screen reader could identify (and made the a11y test flaky,
        // because the mock's site_id is optional).
        aria-label={`Change ${segment.level.toLowerCase()}`}
        onClick={() => setOpen((v) => !v)}
      >
        {segment.label}
        <span style={caretStyle} aria-hidden>&#9662;</span>
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-label="Close"
            style={{ position: "fixed", inset: 0, background: "transparent", border: 0, zIndex: 79 }}
            onClick={() => setOpen(false)}
          />
          <div style={dropdownStyle} role="listbox" aria-label={segment.level}>
            <div style={searchWrapStyle}>
              <input
                ref={inputRef}
                type="text"
                placeholder="Find…"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={searchStyle}
                aria-label={`Filter ${segment.level}`}
              />
            </div>

            {segment.count != null ? (
              <div style={countHeaderStyle}>
                {segment.count} {segment.count === 1 ? "site" : "sites"}
              </div>
            ) : null}

            {recentItems.length > 0 && !filter ? (
              <div style={groupLabelStyle}>Recent</div>
            ) : null}
            {!filter
              ? recentItems.map((it) => (
                  <DropItem
                    key={`recent-${it.id}`}
                    item={it}
                    isSelected={it.id === segment.id}
                    onSelect={() => { onSelect(it.id); setOpen(false); }}
                  />
                ))
              : null}

            {recentItems.length > 0 && !filter ? (
              <div style={groupLabelStyle}>All</div>
            ) : null}
            {filtered.length > 0 ? (
              filtered.map((it) => (
                <DropItem
                  key={it.id}
                  item={it}
                  isSelected={it.id === segment.id}
                  onSelect={() => { onSelect(it.id); setOpen(false); }}
                />
              ))
            ) : (
              <div style={{ padding: "8px 12px", fontSize: 12, color: "var(--sk-color-ink-3)" }}>
                No matches
              </div>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}

function DropItem({
  item,
  isSelected,
  onSelect,
}: {
  item: DropdownItem;
  isSelected: boolean;
  onSelect: () => void;
}): ReactNode {
  return (
    <button
      type="button"
      role="option"
      aria-selected={isSelected}
      style={{
        ...dropItemStyle,
        background: isSelected ? "var(--sk-color-accent-soft)" : "transparent",
        fontWeight: isSelected ? 600 : 400,
      }}
      onClick={onSelect}
    >
      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>
      {item.meta ? <span style={dropItemMetaStyle}>{item.meta}</span> : null}
    </button>
  );
}

/* ── Styles ────────────────────────────────────────────────────────── */

const navStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 2,
};

const parentStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 400,
  color: "var(--sk-color-ink-2)",
  background: "none",
  border: 0,
  cursor: "pointer",
  padding: "2px 4px",
  borderRadius: 4,
  whiteSpace: "nowrap",
  display: "inline-flex",
  alignItems: "center",
  gap: 3,
};

const currentStyle: React.CSSProperties = {
  ...parentStyle,
  fontWeight: 600,
  color: "var(--sk-color-ink-1)",
};

const sepStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--sk-color-ink-3)",
  margin: "0 2px",
  userSelect: "none",
};

const caretStyle: React.CSSProperties = {
  fontSize: 8,
  opacity: 0.5,
};

const dropdownStyle: React.CSSProperties = {
  position: "absolute",
  top: "calc(100% + 4px)",
  left: 0,
  zIndex: 80,
  minWidth: 200,
  maxHeight: 280,
  overflowY: "auto",
  padding: "4px 0",
  background: "var(--sk-color-surface-0)",
  border: "1px solid var(--sk-color-border-1)",
  borderRadius: 8,
  boxShadow: "0 4px 16px rgba(10,10,15,.12)",
};

const searchWrapStyle: React.CSSProperties = {
  padding: "4px 8px 6px",
};

const searchStyle: React.CSSProperties = {
  width: "100%",
  height: 24,
  border: "1px solid var(--sk-color-border-2)",
  borderRadius: 4,
  background: "var(--sk-color-surface-2)",
  color: "var(--sk-color-ink-1)",
  fontSize: 11,
  padding: "0 8px",
  outline: "none",
};

const groupLabelStyle: React.CSSProperties = {
  padding: "6px 12px 2px",
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--sk-color-ink-3)",
};

const dropItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  width: "100%",
  padding: "6px 12px",
  border: 0,
  background: "transparent",
  fontSize: 12,
  color: "var(--sk-color-ink-1)",
  cursor: "pointer",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const dropItemMetaStyle: React.CSSProperties = {
  flexShrink: 0,
  fontSize: 11,
  color: "var(--sk-color-ink-3)",
  fontVariantNumeric: "tabular-nums",
};

const countHeaderStyle: React.CSSProperties = {
  padding: "2px 12px 4px",
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "var(--sk-color-ink-3)",
};
